import { IGameLoadData } from 'State/Game/GameActions';
import System from '../System';
import { LoadEvents, LoadEventType, LoadtimeTrackerSingleton } from 'Utility/LoadtimeTracker';
import Game from 'Game';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { IGameState } from 'State/Game/IGameState';
import { SocialStateHelper } from 'State/Player/Social/SocialStateHelper';
import { IScoreEntry } from 'Utility/SDK/Drivers/IScoreEntry';
import LeaderboardHelper from '../UI/LeaderboardHelper';
import { transitionSceneId } from 'State/Game/GameScene';
import { SignInState } from 'State/Game/SignInState';
import { emitGameEvent } from 'Utility/GameEvent/EmitGameEvent';
import { GameEventName } from 'Utility/GameEvent/GameEventName';
import { startGame } from 'System/Preload/StartGame';
import { queuePreloadAssets } from 'System/Preload/QueuePreloadAssets';
import { initialEventsState } from 'State/Events/initialEventsState';
import { getAllEvents } from 'State/Events/EventHelper';
import { loadAvatar } from '@platform/System/Preload/Helpers/PreloadPlayerAvatar';
import { Logger } from 'Utility/Logger/Logger';
import { LogOrigin } from 'Utility/Logger/LogOrigin';

export default class PreloadSystem extends System {
    private gameLoadedState: IGameLoadData;

    public init(): void {}

    public async preload() {
        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_miscLoading1, LoadEventType.Start);

        queuePreloadAssets();

        // TODO: Why are we pre-loading all the world data?
        WorldDataHelper.queueWorldData();

        const revokeProgressListener = Game.resource.addProgressListener(progress => {
            Game.sdk.loadingProgress(progress);
        });

        // Await preload-assets, player-data, and world-data simultaneously.
        await Promise.all([Game.resource.load(), Game.player.data.restore()]);
        revokeProgressListener();

        Game.style.setStyleData(Game.resource.get('styles'));

        // Save the current game version, once per session.
        if (Game.player.data.playerEndpointData?.version !== Game.config.version) {
            Game.sdk.save({ version: Game.config.version });
        }

        await Game.scene.startScene(transitionSceneId, {}, true);

        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_miscLoading1, LoadEventType.End);

        // Trigger loadingComplete (has its own loadtime metrics)
        await Game.sdk.loadingComplete();

        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_miscLoading2, LoadEventType.Start);

        // Set the language after SDK loadingComplete.
        Game.locale.setLanguage(Game.sdk.getLanguage());

        // Load player avatar once available (after loadingComplete).
        // console.log('loading avatar', Game.sdk.getAvatar());
        // loadAvatar({ key: 'player_avatar', path: Game.sdk.getAvatar() });

        // Initial player state from player data.
        let playerState = Game.player.data.restorePlayerStateFromEndpointData();

        //
        // TODO: Move `LeaderboardHelper.loadTotalStarLeaderboard()` and player-state update to async and new player action in `runAsyncPreloadTasks`.
        // Probably does not need to run sequentially.
        // const friends: IScoreEntry[] = await LeaderboardHelper.loadTotalStarLeaderboard();
        // playerState.social.friendCount = friends.length - 1;
        // playerState.social.friendData = friends.slice(0, 50).map((entry: IScoreEntry) => {
        //     const { friendData } = SocialStateHelper.getFriendData(playerState, entry.user.playerId);
        //     return friendData;
        // });
        Logger.log('Loaded friend data', LogOrigin.PRELOAD);

        // TODO: Don't wait for this, but ensure that once this is removed everything that needs to preload shop still does so after
        await Game.sdk.fetchCatalog();
        Logger.log('Catalog fetched', LogOrigin.PRELOAD);

        // Prepare `GameActions.setLoadedData` payload to be dispatched in `onStart()`.
        this.gameLoadedState = {
            userState: {
                userId: null,
                username: null,
                userAvatar: Game.sdk.getAvatar(),
            },
            platform: {
                //IAPSupport: Game.sdk.hasIAPSupport(),
                adsInterstitial: Game.sdk.hasInterstitialAdSupport(),
                adsRewarded: Game.sdk.hasRewardedAdSupport(),
                switchGame: Game.sdk.canSwitchGame(),
                signInState: SignInState.SignedOut,
            },
            playerState,
            entryPoint: Game.sdk.getEntryPointData(),
            context: Game.sdk.getContextType(),
            eventsState: {
                ...initialEventsState,
                events: getAllEvents(),
            },
        };

        // TODO: Completion events aresent here however does not account for non-awaited methods or other systems in the PreloadScene.
        emitGameEvent({ name: GameEventName.PRELOAD_COMPLETE });
        // LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_miscLoading2, LoadEventType.End);
    }

    protected onDestroy(): void {}

    protected onLateUpdate(): void {}

    protected onStart(): void {
        startGame(this.gameLoadedState);
    }

    protected onStop(): void {}

    protected onUpdate(): void {}

    public handleStateChanged(state: IGameState): void {}
}
